import {Inject, Injectable, Type, DOCUMENT} from "@angular/core";

import {BehaviorSubject} from "rxjs";

export interface FullscreenViewState<T = any>
{
    component: Type<T>;
    data?: any;
    label?: string;
}

export enum CloseMode
{
    Close    = "close",
    CloseAll = "closeAll"
}

@Injectable({providedIn: "root"})
export class FullscreenViewService
{
    private static readonly MAX_STACK_DEPTH  = 4;
    private viewState$                       = new BehaviorSubject<FullscreenViewState | null>(null);
    public currentView$                      = this.viewState$.asObservable();
    private viewStack: FullscreenViewState[] = [];

    constructor(@Inject(DOCUMENT) private document: Document) {}

    private emitTop(): void
    {
        const top = this.viewStack[this.viewStack.length - 1];
        if (top)
        {
            this.document.body.classList.add("cs-fullscreen-view-open");
            this.viewState$.next(top);
        }
        else
        {
            this.document.body.classList.remove("cs-fullscreen-view-open");
            this.viewState$.next(null);
        }
    }

    public show<T>(component: Type<T>,
                   data?: any,
                   label?: string): void
    {
        while (this.viewStack.length >= FullscreenViewService.MAX_STACK_DEPTH)
        {
            this.viewStack.shift();
        }
        this.viewStack.push({
                                component,
                                data,
                                label
                            });
        this.emitTop();
    }

    public close(): void
    {
        this.viewStack.pop();
        this.emitTop();
    }

    public closeAll(): void
    {
        this.viewStack = [];
        this.emitTop();
    }

    public closeWithMode(mode: CloseMode): void
    {
        if (mode === CloseMode.CloseAll)
        {
            this.closeAll();
            return;
        }
        this.close();
    }

    public get currentState(): FullscreenViewState | null
    {
        return this.viewState$.value;
    }

    public updateCurrentData(data: any): void
    {
        const index = this.viewStack.length - 1;
        if (index < 0) return;

        const current         = this.viewStack[index];
        this.viewStack[index] = {
            ...current,
            data: {
                ...(current?.data || {}),
                ...(data || {})
            }
        };
    }

    public getDepth(): number
    {
        return this.viewStack.length;
    }

    public closeToDepth(depth: number): void
    {
        if (depth < 0) depth = 0;

        while (this.viewStack.length > depth)
        {
            this.viewStack.pop();
        }

        this.emitTop();
    }

    public getViewLabels(): string[]
    {
        return this.viewStack
                   .map(v => v.label)
                   .filter((label): label is string => !!label);
    }

    public getOverlayLabels(): string[]
    {
        if (this.viewStack.length <= 1) return [];
        return this.viewStack
                   .slice(0, -1)
                   .map(v => v.label)
                   .filter((label): label is string => !!label);
    }

    public backToView(viewLabel: string): void
    {
        const index = this.viewStack.findIndex(v => v.label === viewLabel);
        if (index >= 0)
        {
            this.closeToDepth(index + 1);
        }
    }
}